import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  LanguageRounded,
  LightbulbOutlineRounded,
  Moving,
  LayersOutlined,
  BoltOutlined,
  HardwareOutlined,
  MemoryOutlined,
  RocketLaunchOutlined,
} from "@mui/icons-material";
import ResourcesAndReusables from "./ResourcesAndReusables.jsx";
import ContentHeader from "../ContentHeader";
import List from "../List.jsx";

import pageData from "../../../../_data/resources-and-reusables.json";

export default function CommercialAndRd() {
  const [data, setData] = useState(null);
  const [selectedTech, setSelectedTech] = useState("");

  const stats = useRef([]);

  const strategicPillars = [
    {
      label: "Standardized Tech Stacks",
      caption: "Uniform delivery through Azure, AWS, and Modern Web frameworks.",
      icon: <LayersOutlined fontSize="large" className="item-icon" />,
    },
    {
      label: "Rapid Prototyping",
      caption: "From concept to MVP in weeks using our JumpStart methodology.",
      icon: <BoltOutlined fontSize="large" className="item-icon" />,
    },
    {
      label: "Technical Governance",
      caption: "Ensuring code quality and architectural integrity across all BU's.",
      icon: <HardwareOutlined fontSize="large" className="item-icon" />,
    },
  ];

  const techStacks = [
    {
      name: "AI & Intelligent Automation",
      icon: <MemoryOutlined fontSize="large" className="item-icon" style={{ color: "#a855f7" }} />,
      items: [
        "AI",
        "Automation",
        "Python",
        "Exploratory Data Analysis",
        "OCR",
        "Selenium",
        "Playwright",
      ],
    },
    {
      name: "Modern Web & Fullstack",
      icon: (
        <RocketLaunchOutlined fontSize="large" className="item-icon" style={{ color: "#3b82f6" }} />
      ),
      items: ["ReactTS", "Fullstack", "Flask", "MUI"],
    },
    {
      name: "Platforms & Infrastructure",
      icon: (
        <HardwareOutlined fontSize="large" className="item-icon" style={{ color: "#f97316" }} />
      ),
      items: ["Integration", "Azure", "AWS", "Microsoft 365", "Power Platform", "ServiceNow"],
    },
  ];

  useEffect(() => {
    const projects = pageData?.projects;

    stats.current = pageData?.stats;

    if (projects) {
      const normalizedData = projects.map((p) => {
        return {
          ...p,
          description: `Source: ${p.engagement} | Work Type: ${p.workType}`,
          projectCategory:
            p.engagement === "R&D" || p.workType === "R&D"
              ? "Research & Development"
              : "Commercial",
          techStack: p.primaryTechStack.length < 1 ? ["General"] : p.primaryTechStack,
        };
      });

      setData(normalizedData);
    }
  }, []);

  const strategicPillarsEl = strategicPillars.map((p) => (
    <li className="strategic-pillar-item" key={p.label}>
      <span>
        {p.icon}
        <div>
          <h4>{p.label}</h4>
          <p>{p.caption}</p>
        </div>
      </span>
    </li>
  ));

  const filteredData = useMemo(() => {
    if (selectedTech === "" || !selectedTech) return data;
    return data.filter((p) => p.techStack.includes(selectedTech));
  }, [data, selectedTech]);

  if (!data) return <p>Loading</p>;

  return (
    <>
      <ResourcesAndReusables />

      <ContentHeader
        title={"Commercial & R&D"}
        caption={"The bridge between cutting-edge research and world-class commercial delivery."}
      />

      <div className="rd-hero">
        <div>
          <h1>Powering Digital Transformation</h1>
          <p>
            We specialize in transforming conceptual research into scalable, production-ready
            solutions that drive value for our global clients.
          </p>

          <span className="commercial-cards">
            <div className="commercial-card">
              <LanguageRounded
                fontSize="large"
                className="commercial-card-icon commercial-excellence"
              />
              <div>
                <h4>Commercial Excellence</h4>
                <p>Global delivery standards and best practices.</p>
              </div>
            </div>

            <div className="commercial-card">
              <LightbulbOutlineRounded
                fontSize="large"
                className="commercial-card-icon innovation-hub"
              />
              <div>
                <h4>R&D Innovation Hub</h4>
                <p>Exploring the next frontier of tech.</p>
              </div>
            </div>
          </span>
        </div>

        <div className="strategic-pillars">
          <span className="strategic-pillars-span">
            <Moving fontSize="large" className="icon" />
            <h3>Our Strategic Pillars</h3>
          </span>
          <ul className="pillar-list">{strategicPillarsEl}</ul>
        </div>
      </div>

      <div className="stat-track">
        {stats.current &&
          stats.current.map((s, i) => (
            <div key={i} className="stat-track-item">
              <div className="stat-track-value">{s.value}</div>
              <div className="stat-track-label">{s.label}</div>
            </div>
          ))}
      </div>

      <div className="portfolio-and-expertise">
        <h2>Portfolio & Expertise</h2>
        <p>
          Explore our active portfolio and research initiatives. Use the capability filters to drill
          down into specific technological expertise.
        </p>
      </div>

      <div className="capabilities">
        <div className="filters">
          <span className="filters-header">
            <h2>Capabilities</h2>
            {selectedTech && <button onClick={() => setSelectedTech(null)}>Clear Filter</button>}
          </span>
          {techStacks.map((t) => {
            return (
              <div key={t.name} className="filter-category">
                <span>
                  {t.icon}
                  <h3>{t.name}</h3>
                </span>

                <ul>
                  {t.items.map((i) => {
                    const isActive = selectedTech === i;
                    const count = data.filter((p) => p.techStack.includes(i)).length;

                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedTech(isActive ? null : i)}
                        className={`category-filter-button ${isActive ? "selected" : ""}`}
                      >
                        <p>{i}</p>
                        <span className="project-count">
                          <p>{count}</p>
                        </span>
                      </button>
                    );
                  })}
                </ul>
              </div>
            );
          })}
          <div className="filter-category cross-bu-solutions">
            <h3>Cross-BU Solutions</h3>
            <p>
              We deliver end-to-end solutions across diverse business units using these standardized
              stacks.
            </p>
            <button>View Case Studies</button>
          </div>
        </div>
        <div className="active-portfolio">
          <span className="portfolio-header">
            <h3>Active Portfolio</h3>
            {selectedTech && <p>{selectedTech}</p>}
          </span>
          {selectedTech ? (
            <p>
              Showing {filteredData.length} projects using {selectedTech} expertise.
            </p>
          ) : (
            <p>Total of {data.length} active engagements across the stack.</p>
          )}
          <List
            data={filteredData}
            itemNameKey={"title"}
            itemExcerptKey={"description"}
            listFilterKey={"projectCategory"}
            listFilterVals={["Research & Development", "Commercial"]}
            itemFields={[
              {
                key: "techStack",
                label: "Expertise",
                type: "chip",
              },
              {
                key: "status",
                label: "Status",
                type: "chip",
              },
            ]}
          />
        </div>
      </div>
    </>
  );
}
